import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "searchGroup",
        "searchInput",
        "status",
        "manualToggle",
        "details",
        "streetLine1",
        "streetLine2",
        "city",
        "state",
        "stateCode",
        "postalCode",
        "country",
        "countryCode",
        "latitude",
        "longitude"
    ]

    static values = {
        url: String
    }

    connect() {
        this.boundOnInput = this.onInput.bind(this)
        this.boundOnBlur = this.onBlur.bind(this)
        this.boundOnFocus = this.onFocus.bind(this)
        this.boundOnKeydown = this.onKeydown.bind(this)
        this.boundPositionResults = this.positionVisibleResults.bind(this)

        if (!this.hasSearchInputTarget) return

        this.results = document.createElement("div")
        this.results.id = `address-search-results-${Math.random().toString(36).slice(2)}`
        this.results.className = "address-search-results list-group shadow"
        this.results.setAttribute("role", "listbox")
        // Keep suggestions interactive inside modal dialogs and above clipped
        // containers. A z-index on the page cannot reach a dialog's top layer.
        this.results.setAttribute("popover", "manual")
        this.results.style.display = "none"
        const resultsContainer = this.element.closest("dialog") || document.body
        resultsContainer.appendChild(this.results)

        this.searchInputTarget.setAttribute("aria-controls", this.results.id)
        this.searchInputTarget.setAttribute("aria-expanded", "false")

        this.timer = null
        this.requestId = 0
        this.abortController = null
        this.manualEntry = false
        this.originalSearchRequired = this.searchInputTarget.required
        this.selectedAddress = this.searchInputTarget.value || ""

        this.syncDetailsVisibility()
        this.searchInputTarget.addEventListener("input", this.boundOnInput)
        this.searchInputTarget.addEventListener("blur", this.boundOnBlur)
        this.searchInputTarget.addEventListener("focus", this.boundOnFocus)
        this.searchInputTarget.addEventListener("keydown", this.boundOnKeydown)
        window.addEventListener("resize", this.boundPositionResults)
        document.addEventListener("scroll", this.boundPositionResults, true)
    }

    disconnect() {
        this.cancelPendingSearch()
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.removeEventListener("input", this.boundOnInput)
            this.searchInputTarget.removeEventListener("blur", this.boundOnBlur)
            this.searchInputTarget.removeEventListener("focus", this.boundOnFocus)
            this.searchInputTarget.removeEventListener("keydown", this.boundOnKeydown)
        }
        window.removeEventListener("resize", this.boundPositionResults)
        document.removeEventListener("scroll", this.boundPositionResults, true)
        this.results?.remove()
    }

    onInput(event) {
        clearTimeout(this.timer)
        this.abortCurrentRequest()

        const query = event.target.value.trim()

        if (event.target.value !== this.selectedAddress) {
            this.clearSelectedAddressFields()
            this.hideDetails()
            this.clearValidationErrors()
        }

        if (query.length < 3) {
            this.hideResults()
            this.setStatus("Type at least 3 characters. Add the suburb or postcode for better matches.")
            return
        }

        this.setStatus("Waiting for you to finish typing…")
        this.timer = setTimeout(() => this.search(query), 300)
    }

    search(query) {
        const endpoint = this.searchEndpoint(query)
        if (!endpoint) return

        this.abortCurrentRequest()
        this.abortController = new AbortController()
        const currentRequestId = ++this.requestId
        this.setStatus("Searching addresses…")

        fetch(endpoint, {
            headers: { Accept: "application/json" },
            signal: this.abortController.signal
        })
            .then(response => {
                if (!response.ok) throw new Error(`Address search failed with ${response.status}`)
                return response.json()
            })
            .then(data => {
                if (currentRequestId !== this.requestId) return
                this.render(Array.isArray(data) ? data : [])
            })
            .catch(error => {
                if (error.name === "AbortError" || currentRequestId !== this.requestId) return
                this.hideResults()
                this.setStatus("Address search is unavailable. You can enter the address manually.")
            })
            .finally(() => {
                if (currentRequestId === this.requestId) this.abortController = null
            })
    }

    searchEndpoint(query) {
        return this.endpointWithParams({ q: query })
    }

    placeDetailsEndpoint(placeId) {
        return this.endpointWithParams({ place_id: placeId })
    }

    endpointWithParams(params) {
        const baseUrl = this.hasUrlValue ? this.urlValue : (window.slug ? `/${window.slug}/addresses/search` : null)
        if (!baseUrl) return null

        const endpoint = new URL(baseUrl, window.location.origin)
        Object.entries(params).forEach(([key, value]) => endpoint.searchParams.set(key, value))
        return endpoint.toString()
    }

    render(results) {
        this.results.innerHTML = ""

        if (results.length === 0) {
            this.hideResults()
            this.setStatus("No matching addresses found. Add a suburb or postcode, or enter the address manually.")
            return
        }

        results.forEach(result => {
            const button = document.createElement("button")
            button.type = "button"
            button.className = "list-group-item list-group-item-action text-start"
            button.setAttribute("role", "option")
            this.renderResultLabel(button, result)
            button.onmousedown = event => event.preventDefault()
            button.onclick = () => this.select(result)
            this.results.appendChild(button)
        })

        if (results.some(result => result.provider === "google_maps")) this.renderGoogleMapsAttribution()

        this.showResults()
        this.searchInputTarget.setAttribute("aria-expanded", "true")
        this.setStatus(`${results.length} matching ${results.length === 1 ? "address" : "addresses"} found.`)
    }

    select(result) {
        if (result.place_id) {
            this.resolvePlace(result)
            return
        }

        this.applySelection(result)
    }

    resolvePlace(prediction) {
        const endpoint = this.placeDetailsEndpoint(prediction.place_id)
        if (!endpoint) return

        this.cancelPendingSearch()
        this.searchInputTarget.value = prediction.full_address || ""
        this.selectedAddress = prediction.full_address || ""
        this.hideResults()
        this.abortController = new AbortController()
        const currentRequestId = ++this.requestId
        this.setStatus("Loading the selected address…")

        fetch(endpoint, {
            headers: { Accept: "application/json" },
            signal: this.abortController.signal
        })
            .then(response => {
                if (!response.ok) throw new Error(`Address details failed with ${response.status}`)
                return response.json()
            })
            .then(result => {
                if (currentRequestId !== this.requestId) return
                this.applySelection(result)
            })
            .catch(error => {
                if (error.name === "AbortError" || currentRequestId !== this.requestId) return
                this.setStatus("We could not load that address. Try another match or enter it manually.")
            })
            .finally(() => {
                if (currentRequestId === this.requestId) this.abortController = null
            })
    }

    applySelection(result) {
        this.manualEntry = false
        this.searchInputTarget.value = result.full_address || ""
        this.searchInputTarget.required = this.originalSearchRequired
        this.selectedAddress = result.full_address || ""
        this.setTargetValue("streetLine1", result.street_line1)
        this.setTargetValue("streetLine2", result.street_line2)
        this.setTargetValue("city", result.city)
        this.setTargetValue("state", result.state)
        this.setTargetValue("stateCode", result.state_code)
        this.setTargetValue("postalCode", result.postal_code)
        this.setTargetValue("country", result.country)
        this.setTargetValue("countryCode", result.country_code)
        this.setTargetValue("latitude", result.latitude)
        this.setTargetValue("longitude", result.longitude)

        this.setAddressFieldsReadOnly(true)
        this.showDetails()
        this.hideResults()
        this.updateManualToggle()
        this.clearValidationErrors()
        this.setStatus("Address selected. Choose manual entry if you need to add or correct details.")
    }

    renderResultLabel(button, result) {
        if (!result.primary_text) {
            button.textContent = result.full_address
            return
        }

        const primary = document.createElement("span")
        primary.className = "d-block fw-semibold"
        primary.textContent = result.primary_text
        button.appendChild(primary)

        if (result.secondary_text) {
            const secondary = document.createElement("span")
            secondary.className = "d-block small fg-2"
            secondary.textContent = result.secondary_text
            button.appendChild(secondary)
        }
    }

    renderGoogleMapsAttribution() {
        const attribution = document.createElement("div")
        attribution.className = "address-search-attribution px-3 py-2 text-end small fg-2"
        attribution.setAttribute("translate", "no")
        attribution.setAttribute("aria-label", "Suggestions provided by Google Maps")
        attribution.textContent = "Google Maps"
        this.results.appendChild(attribution)
    }

    toggleManualEntry() {
        if (this.manualEntry) {
            this.useAddressSearch()
        } else {
            this.enableManualEntry()
        }
    }

    enableManualEntry() {
        this.cancelPendingSearch()
        this.manualEntry = true
        this.hideResults()
        this.searchGroupTarget.classList.add("d-none")
        this.searchInputTarget.required = false
        this.setAddressFieldsReadOnly(false)

        if (!this.lookupTarget("country")?.value) this.setTargetValue("country", "Australia")
        if (!this.lookupTarget("countryCode")?.value) this.setTargetValue("countryCode", "AU")

        this.showDetails()
        this.updateManualToggle()
        this.clearValidationErrors()
        this.setStatus("Manual entry enabled. Complete the street, city, state, postcode, and country fields.")
        this.lookupTarget("streetLine1")?.focus()
    }

    useAddressSearch() {
        this.manualEntry = false
        this.searchGroupTarget.classList.remove("d-none")
        this.searchInputTarget.required = this.originalSearchRequired
        this.searchInputTarget.value = ""
        this.selectedAddress = ""
        this.clearSelectedAddressFields()
        this.setAddressFieldsReadOnly(true)
        this.hideDetails()
        this.updateManualToggle()
        this.setStatus("Type at least 3 characters. Add the suburb or postcode for better matches.")
        this.searchInputTarget.focus()
    }

    updateManualToggle() {
        if (!this.hasManualToggleTarget) return

        this.manualToggleTarget.textContent = this.manualEntry ? "Use address search" : "Enter address manually"
        this.manualToggleTarget.setAttribute("aria-pressed", this.manualEntry ? "true" : "false")
    }

    setAddressFieldsReadOnly(readOnly) {
        ["streetLine1", "streetLine2", "city", "state", "postalCode", "country"].forEach(targetName => {
            const target = this.lookupTarget(targetName)
            if (target) target.readOnly = readOnly
        })
    }

    setTargetValue(targetName, value) {
        const target = this.lookupTarget(targetName)
        if (target) target.value = value || ""
    }

    lookupTarget(targetName) {
        const targetProperty = `${targetName}Target`
        const hasTargetProperty = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`

        return this[hasTargetProperty] ? this[targetProperty] : null
    }

    onBlur() {
        setTimeout(() => {
            if (!this.results.contains(document.activeElement)) this.hideResults(false)
        }, 150)
    }

    onFocus() {
        if (this.results.childElementCount === 0) return

        this.showResults()
        this.searchInputTarget.setAttribute("aria-expanded", "true")
    }

    onKeydown(event) {
        if (event.key === "Escape") {
            this.hideResults(false)
            return
        }

        if (event.key === "ArrowDown" && this.results.style.display !== "none") {
            event.preventDefault()
            this.results.querySelector("button")?.focus()
        }
    }

    hideResults(clear = true) {
        if (!this.results) return

        if (clear) this.results.innerHTML = ""
        this.results.hidePopover?.()
        this.results.style.display = "none"
        this.searchInputTarget?.setAttribute("aria-expanded", "false")
    }

    showResults() {
        this.results.style.display = "block"
        this.results.showPopover?.()
        this.positionResults()
    }

    positionVisibleResults() {
        if (this.results?.style.display !== "none") this.positionResults()
    }

    positionResults() {
        if (!this.hasSearchInputTarget || !this.searchInputTarget.isConnected) return

        const rect = this.searchInputTarget.getBoundingClientRect()
        const viewportPadding = 8
        const gap = 4
        const width = Math.min(rect.width, window.innerWidth - (viewportPadding * 2))
        const left = Math.min(
            Math.max(viewportPadding, rect.left),
            window.innerWidth - width - viewportPadding
        )
        const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding
        const spaceAbove = rect.top - gap - viewportPadding
        const openAbove = spaceBelow < 180 && spaceAbove > spaceBelow
        const availableHeight = openAbove ? spaceAbove : spaceBelow

        this.results.style.left = `${left}px`
        this.results.style.top = `${openAbove ? rect.top - gap : rect.bottom + gap}px`
        this.results.style.width = `${width}px`
        this.results.style.maxHeight = `${Math.max(96, Math.min(352, availableHeight))}px`
        this.results.style.transform = openAbove ? "translateY(-100%)" : "none"
    }

    setStatus(message) {
        if (this.hasStatusTarget) this.statusTarget.textContent = message
    }

    cancelPendingSearch() {
        clearTimeout(this.timer)
        this.abortCurrentRequest()
    }

    abortCurrentRequest() {
        this.abortController?.abort()
        this.abortController = null
        this.requestId += 1
    }

    syncDetailsVisibility() {
        if (this.persistedAddressSelected()) {
            this.showDetails()
        } else {
            this.hideDetails()
        }
    }

    persistedAddressSelected() {
        return [
            this.lookupTarget("streetLine1")?.value,
            this.lookupTarget("city")?.value,
            this.lookupTarget("state")?.value,
            this.lookupTarget("postalCode")?.value
        ].some(value => value && value.length > 0)
    }

    clearSelectedAddressFields() {
        this.selectedAddress = ""
        this.setTargetValue("streetLine1", "")
        this.setTargetValue("streetLine2", "")
        this.setTargetValue("city", "")
        this.setTargetValue("state", "")
        this.setTargetValue("stateCode", "")
        this.setTargetValue("postalCode", "")
        this.setTargetValue("country", "")
        this.setTargetValue("countryCode", "")
        this.setTargetValue("latitude", "")
        this.setTargetValue("longitude", "")
    }

    clearValidationErrors() {
        this.clearSearchInputValidationError()
        this.clearValidationNotification()
    }

    addressValidationMessages() {
        return [
            "Full address must be selected from the address search",
            "Full address must include street, city, state, postal code, and country"
        ]
    }

    clearSearchInputValidationError() {
        const wrapper = this.searchInputTarget.closest(".mb-3")
        if (!wrapper) return

        this.searchInputTarget.classList.remove("is-invalid")
        this.searchInputTarget.removeAttribute("aria-invalid")

        wrapper.querySelectorAll(".invalid-feedback").forEach(feedback => {
            if (this.addressValidationMessages().some(message => feedback.textContent?.includes(message))) feedback.remove()
        })
    }

    clearValidationNotification() {
        const form = this.element.closest("form")
        if (!form) return

        form.querySelectorAll(".error_notification, .alert.theme-danger").forEach(notification => {
            const text = notification.textContent?.trim()
            if (!text || !this.addressValidationMessages().some(message => text.includes(message))) return

            const cleanedText = this.addressValidationMessages().reduce(
                (value, message) => this.removeAddressValidationMessage(value, message),
                text
            )

            if (cleanedText.length > 0) {
                notification.textContent = cleanedText
            } else {
                notification.remove()
            }
        })
    }

    removeAddressValidationMessage(text, message) {
        const escapedMessage = message.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

        return text
            .replace(new RegExp(`(?:,\\s*)?and\\s+${escapedMessage}\\.?$`, "i"), "")
            .replace(new RegExp(`(?:,\\s*)?${escapedMessage}\\.?$`, "i"), "")
            .replace(/\s+(?:and|or)\s*$/i, "")
            .replace(/[,\s]+$/g, "")
            .trim()
    }

    showDetails() {
        this.detailsTargets.forEach(target => {
            if (this.manualEntry) {
                target.classList.remove("d-none")
                return
            }

            const fieldTarget = this.lookupTarget(target.dataset.addressSearchField)
            const hasValue = fieldTarget && fieldTarget.value && fieldTarget.value.length > 0
            target.classList.toggle("d-none", !hasValue)
        })
    }

    hideDetails() {
        this.detailsTargets.forEach(target => target.classList.add("d-none"))
    }
}
